/**
 * Image library — high-quality stock used across the Pfizer-style layout.
 *
 * Owner directive (2026-02): every photo must be visibly relevant to what
 * NoJo actually does — ETC, tollgates, Japanese expressways, driving in
 * Japan, toll-record paperwork, yen exchange. No generic lifestyle stock,
 * no tourists, no "happy military family" filler photos, no beakers or
 * cafe scenes pretending to be currency.
 *
 * Verified-stable photo: `photo-1480714378408-67cf0d13bc1b` is a Japanese
 * urban expressway with traffic — it is the one stock photo we trust to
 * be on-brand. All "needs a Japan-road" slots reuse it. The other slots
 * are intentionally left as CSS-driven brand panels rendered in-component
 * rather than gamble on un-curated stock IDs.
 */
export const IMG = {
  // Yokosuka ETC toll booth — owner-provided photograph (May 2026).
  // Hyper on-brand: this is exactly the Yokosuka tollgate that JEC
  // customers use most often, and it grounds the homepage hero in our
  // hometown rather than generic stock imagery.
  heroBanner: "/images/yokosuka-toll-hero.webp",
  article1:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=1200&q=80",
  article2:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=1200&q=80",
  article3:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=1200&q=80",
  about:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=1600&q=80",
  // Audience tiles — every one reuses the verified expressway shot for now.
  audienceForeign:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=900&q=80",
  audienceMilitary:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=900&q=80",
  audienceFleet:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=900&q=80",
  audienceJapanese:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=900&q=80",
  // Commitment band — Yokosuka tollgate gateway shot (owner-provided
  // photograph, May 2026). Reinforces the "built from Yokosuka" message
  // by anchoring the commitment band in the literal Yokosuka tollgate
  // that our JEC customers pass through daily.
  commitment: "/images/yokosuka-toll-commitment.webp",
  // Service-detail editorial — verified Japan expressway for ETC + TollRcpts.
  // JapanChangeMoney intentionally has no stock photo; the detail page uses
  // a CSS brand panel instead (see ServiceDetailPage.jsx).
  detailEtcLane:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=1600&q=80",
  detailTunnel:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=1600&q=80",
  detailReceipts:
    "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?auto=format&fit=crop&w=1600&q=80",
  // JapanETCcard sample card artwork — used on /services JapanETCcard section
  japanetccardSample: "/brand/japanetccard-sample.png",
  // Product wordmarks (transparent PNGs — wordmark only, no slogan text baked in)
  japanetccardWordmark: "/brand/japanetccard-wordmark.png",
  japantollreceiptsWordmark: "/brand/japantollreceipts-wordmark.png",
  // Owner-uploaded clean JCM text-only wordmark (replaces the old slogan-baked PNG)
  japanchangemoneyWordmark:
    "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/n3hg8is0_JapanChangeMoney_TextOnly.png",
  // Owner-uploaded JCM icon (two hands trading € $ ¥ £) — brand anchor for JCM
  japanchangemoneyIcon:
    "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/yx1hkjjf_logo%20only.png",
  // Icon + wordmark side-by-side (for places where the combined lockup is preferred)
  japanchangemoneyLockup:
    "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/op2d3j69_New%20Logo%20on%20Site%20With%20JapanChangeMoney%20-%20High%20Resolution%20Big.png",
  // Illustrated ETC lane scene (car + gate) — for marketing context blocks
  etcLane: "/brand/etc-lane.webp",
  // Owner-uploaded transparent ETC gate illustration — the brand anchor for
  // BOTH JapanETCcard and JapanTollReceipts. Always paired with the wordmark.
  etcGateBrand:
    "https://customer-assets.emergentagent.com/job_173ca005-b116-4411-b1d2-268a4ccdf2f7/artifacts/ng8fd8zo_ETCLane_Transparent.png",
};
